import * as XLSX from "xlsx";

import { DocType } from "../../graphql/generated";
import { DocumentType, ForgeryStatus } from "../IDPForgeryCheck/types";
import {
  ForgeryCheckType,
  JSONArray,
  JSONObject,
  JSONPrimitive,
  JSONValue,
} from "../IDPForgeryCheck/types";
import { ForgeryResult } from "../IDPForgeryCheck/types";

export const mapDocumentTypeToDocType = (
  documentType: DocumentType
): DocType => {
  switch (documentType) {
    case DocumentType.KTP:
      return DocType.Ktp;
    case DocumentType.NPWP_COMPANY:
      return DocType.NpwpCompany;
    case DocumentType.NPWP_INDIVIDU:
      return DocType.NpwpIndividu;
    case DocumentType.REKENING_KORAN:
      return DocType.RekeningKoran;
    case DocumentType.FAKTUR_PAJAK:
      return DocType.FakturPajak;
    case DocumentType.INVOICE:
      return DocType.Invoice;
    case DocumentType.DOKUMEN_PO:
      return DocType.PurchaseOrder;
    default:
      throw new Error(`Unsupported document type: ${documentType}`);
  }
};

export const mapDocTypeToDocumentType = (docType: DocType): DocumentType => {
  switch (docType) {
    case DocType.Ktp:
      return DocumentType.KTP;
    case DocType.NpwpCompany:
      return DocumentType.NPWP_COMPANY;
    case DocType.NpwpIndividu:
      return DocumentType.NPWP_INDIVIDU;
    case DocType.RekeningKoran:
      return DocumentType.REKENING_KORAN;
    case DocType.FakturPajak:
      return DocumentType.FAKTUR_PAJAK;
    case DocType.Invoice:
      return DocumentType.INVOICE;
    case DocType.PurchaseOrder:
      return DocumentType.DOKUMEN_PO;
    default:
      throw new Error(`Unsupported doc type: ${docType}`);
  }
};

export const isJSONPrimitive = (value: JSONValue): value is JSONPrimitive => {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
};

export const isJSONArray = (value: JSONValue): value is JSONArray => {
  return Array.isArray(value);
};

export const isJSONObject = (value: JSONValue): value is JSONObject => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const isValidJSONValue = (value: unknown): value is JSONValue => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isValidJSONValue);
  }
  if (typeof value === "object" && value !== null) {
    return Object.values(value).every(isValidJSONValue);
  }
  return false;
};

export const CHECK_METHOD_TO_CATEGORY: Record<string, ForgeryCheckType> = {
  "margin check": ForgeryCheckType.FORMAT_INCONSISTENCY,
  "typo check": ForgeryCheckType.FORMAT_INCONSISTENCY,
  "format check": ForgeryCheckType.FORMAT_INCONSISTENCY,
  "font check": ForgeryCheckType.FORMAT_INCONSISTENCY,
  "Pdf Font Checker": ForgeryCheckType.FORMAT_INCONSISTENCY,

  "logic check": ForgeryCheckType.LOGICAL_FLAW,
  "Logic Checker": ForgeryCheckType.LOGICAL_FLAW,
  "semantic check": ForgeryCheckType.LOGICAL_FLAW,
  "consistency check": ForgeryCheckType.LOGICAL_FLAW,

  "PDF Metadata": ForgeryCheckType.METADATA_CHECK,
  "metadata check": ForgeryCheckType.METADATA_CHECK,
  EXIF: ForgeryCheckType.METADATA_CHECK,
  "PDF xrf update": ForgeryCheckType.METADATA_CHECK,
  "Image Metadata Analysis": ForgeryCheckType.METADATA_CHECK,

  "ELA Analysis": ForgeryCheckType.IMAGE_ANOMALY,
  "Image ELA Analysis": ForgeryCheckType.IMAGE_ANOMALY,
  "image anomaly": ForgeryCheckType.IMAGE_ANOMALY,
  "AI Image Detector": ForgeryCheckType.IMAGE_ANOMALY,
  "image manipulation": ForgeryCheckType.IMAGE_ANOMALY,
};

export const DOCUMENT_FORGERY_CATEGORIES: Record<
  DocumentType,
  ForgeryCheckType[]
> = {
  [DocumentType.KTP]: [
    ForgeryCheckType.IMAGE_ANOMALY,
    ForgeryCheckType.LOGICAL_FLAW,
    ForgeryCheckType.FORMAT_INCONSISTENCY,
  ],
  [DocumentType.NPWP_COMPANY]: [
    ForgeryCheckType.METADATA_CHECK,
    ForgeryCheckType.FORMAT_INCONSISTENCY,
    ForgeryCheckType.LOGICAL_FLAW,
  ],
  [DocumentType.NPWP_INDIVIDU]: [
    ForgeryCheckType.METADATA_CHECK,
    ForgeryCheckType.FORMAT_INCONSISTENCY,
    ForgeryCheckType.LOGICAL_FLAW,
  ],
  [DocumentType.REKENING_KORAN]: [
    ForgeryCheckType.LOGICAL_FLAW,
    ForgeryCheckType.METADATA_CHECK,
    ForgeryCheckType.FORMAT_INCONSISTENCY,
  ],
  [DocumentType.DOKUMEN_PO]: [ForgeryCheckType.FORMAT_INCONSISTENCY],
  [DocumentType.INVOICE]: [ForgeryCheckType.FORMAT_INCONSISTENCY],
  [DocumentType.FAKTUR_PAJAK]: [
    ForgeryCheckType.LOGICAL_FLAW,
    ForgeryCheckType.METADATA_CHECK,
    ForgeryCheckType.FORMAT_INCONSISTENCY,
  ],
};

export const getCategoryFromMethod = (method: string): ForgeryCheckType => {
  const normalizedMethod = method.toLowerCase().trim();

  if (CHECK_METHOD_TO_CATEGORY[method]) {
    return CHECK_METHOD_TO_CATEGORY[method];
  }

  const exactMatch = Object.keys(CHECK_METHOD_TO_CATEGORY).find(
    (key) => key.toLowerCase() === normalizedMethod
  );
  if (exactMatch) {
    return CHECK_METHOD_TO_CATEGORY[exactMatch];
  }

  if (
    normalizedMethod.includes("margin") ||
    normalizedMethod.includes("typo") ||
    normalizedMethod.includes("format") ||
    normalizedMethod.includes("font")
  ) {
    return ForgeryCheckType.FORMAT_INCONSISTENCY;
  }

  if (
    normalizedMethod.includes("logic") ||
    normalizedMethod.includes("semantic") ||
    normalizedMethod.includes("consistency")
  ) {
    return ForgeryCheckType.LOGICAL_FLAW;
  }

  if (
    normalizedMethod.includes("metadata") ||
    normalizedMethod.includes("exif") ||
    normalizedMethod.includes("pdf")
  ) {
    return ForgeryCheckType.METADATA_CHECK;
  }

  if (
    normalizedMethod.includes("image") ||
    normalizedMethod.includes("ela") ||
    normalizedMethod.includes("ai")
  ) {
    return ForgeryCheckType.IMAGE_ANOMALY;
  }

  return ForgeryCheckType.FORMAT_INCONSISTENCY;
};

export const isObject = (value: JSONValue): value is JSONObject => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

export const getDataType = (value: JSONValue): string => {
  if (value === null || value === undefined) return "null";
  if (Array.isArray(value)) return "array";
  if (isObject(value)) return "object";
  return "primitive";
};

export const deepClone = <T extends JSONValue>(obj: T): T => {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => deepClone(item)) as T;

  const cloned: Record<string, JSONValue> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone((obj as JSONObject)[key]);
    }
  }
  return cloned as T;
};

const parseNumericValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const convertToCSV = (data: JSONValue): string => {
  if (!isJSONObject(data)) return "";

  const flattenObject = (
    obj: JSONObject,
    prefix = ""
  ): Record<string, string> => {
    const flattened: Record<string, string> = {};

    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value === null || value === undefined) {
        flattened[newKey] = "";
      } else if (isJSONArray(value)) {
        if (value.length === 0) {
          flattened[newKey] = "";
        } else if (isJSONObject(value[0])) {
          flattened[newKey] = `${value.length} items`;
          if (value[0]) {
            const firstItem = flattenObject(
              value[0] as JSONObject,
              `${newKey}[0]`
            );
            Object.assign(flattened, firstItem);
          }
        } else {
          flattened[newKey] = value.map((v) => String(v)).join(", ");
        }
      } else if (isJSONObject(value)) {
        const nested = flattenObject(value, newKey);
        Object.assign(flattened, nested);
      } else {
        flattened[newKey] = String(value);
      }
    });

    return flattened;
  };

  const flattened = flattenObject(data);
  const headers = Object.keys(flattened);
  const values = Object.values(flattened);

  return [
    headers.join(","),
    values.map((v) => `"${v.replace(/"/g, '""')}"`).join(","),
  ].join("\n");
};

export const downloadCSV = (data: JSONValue, filename: string): void => {
  const csvContent = convertToCSV(data);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const downloadExcel = (
  data: JSONValue,
  filename: string,
  fd: ForgeryResult | null
): void => {
  console.log(fd);

  if (!isJSONObject(data)) return;

  const workbook = XLSX.utils.book_new();

  const transactions = data.transactions as JSONArray | undefined;

  if (transactions && isJSONArray(transactions) && transactions.length > 0) {
    const allData: (string | number | null)[][] = [];

    if (fd) {
      allData.push(["FORGERY DETECTION REPORT"]);
      allData.push([]);

      const statusEmoji =
        fd.status === ForgeryStatus.CLEAN
          ? "✓"
          : fd.status === ForgeryStatus.FORGED
          ? "✗"
          : "?";
      allData.push(["Status:", `${statusEmoji} ${fd.status}`]);
      allData.push(["Message:", fd.message]);
      allData.push([]);
      allData.push([]);
    }

    const columnOrder = [
      "transaction_date",
      "description",
      "reference",
      "debit",
      "credit",
      "balance",
    ];

    allData.push(columnOrder);

    (transactions as Record<string, unknown>[]).forEach((transaction) => {
      const row = columnOrder.map((col) => {
        const value = transaction[col];

        if (col === "debit" || col === "credit" || col === "balance") {
          return parseNumericValue(value);
        }

        return value ?? "";
      });

      // @ts-expect-error row is correctly typed
      allData.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(allData);

    sheet["!cols"] = [
      { wch: 15 }, // transaction_date
      { wch: 40 }, // description
      { wch: 20 }, // reference
      { wch: 15 }, // debit
      { wch: 15 }, // credit
      { wch: 15 }, // balance
    ];

    const headerRowIndex = fd ? 6 : 0;
    const dataStartRow = headerRowIndex + 1;

    for (let rowIndex = dataStartRow; rowIndex < allData.length; rowIndex++) {
      // Format debit column (column D)
      const debitCell = XLSX.utils.encode_cell({ r: rowIndex, c: 3 });
      if (sheet[debitCell] && typeof sheet[debitCell].v === "number") {
        sheet[debitCell].t = "n";
        sheet[debitCell].z = "#,##0.00";
      }

      // Format credit column (column E)
      const creditCell = XLSX.utils.encode_cell({ r: rowIndex, c: 4 });
      if (sheet[creditCell] && typeof sheet[creditCell].v === "number") {
        sheet[creditCell].t = "n";
        sheet[creditCell].z = "#,##0.00";
      }

      // Format balance column (column F)
      const balanceCell = XLSX.utils.encode_cell({ r: rowIndex, c: 5 });
      if (sheet[balanceCell] && typeof sheet[balanceCell].v === "number") {
        sheet[balanceCell].t = "n";
        sheet[balanceCell].z = "#,##0.00";
      }
    }

    XLSX.utils.book_append_sheet(workbook, sheet, "Transactions");
  }

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};
